import { useEffect, useState } from 'react';
import type { DatePickerProps } from 'antd';
import { Button, DatePicker, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMerchantsStore } from 'store/merchants';
import { usePaymentInvoicesTableStore } from 'store/paymentInvoicesTable';
import { usePaymentInvoicesTableFiltersStore } from 'store/paymentInvoicesTableFilters';
import MultiSelect from 'ui/controls/MultiSelect';
import { Search } from 'ui/controls/Search';
import SingleSelect from 'ui/controls/SingleSelect';
import { dateHelpers } from 'utils/helpers/dateHelpers';
import { predicates } from 'utils/helpers/predicates';
import type { TComponent } from 'utils/types/component';

import omitBy from 'lodash/omitBy';

import { InvoicesExportModalButton } from 'components/domains/Invoices/components/InvoicesExportModalButton';
import { useDateValidation } from 'components/utils/hooks/useDateValidation';

import { InvoicesExportSettingsModalButton } from '../InvoicesExportSettingsModalButton';
import styles from './Filters.module.scss';
import { filtersHelpers } from './helpers';
import type { IFilterForm } from './types';

const INITIAL_FILTERS: IFilterForm = {
    searchStr: '',
    externalUserId: '',
    externalIdentity: '',
    currencyCode: null,
    createdAtFrom: '',
    createdAtTo: '',
    amountFrom: '',
    amountTo: '',
    finishedAtFrom: '',
    finishedAtTo: '',
    surchargeAmountFrom: '',
    surchargeAmountTo: '',
    statuses: [],
    methodCodes: [],
    siteIdentities: [],
    merchantIdentities: [],
};

interface IFiltersProps {
    onExport: (email: string) => void;
}

export const Filters: TComponent<IFiltersProps> = (props) => {
    const { onExport } = props;

    const { t } = useTranslation();

    const { isValidDateRange } = useDateValidation();

    const [form] = Form.useForm<IFilterForm>();
    const [formData, setFormData] = useState<IFilterForm>(INITIAL_FILTERS);

    const params = usePaymentInvoicesTableFiltersStore('params');
    const clearBody = usePaymentInvoicesTableFiltersStore('clearBody');
    const setBody = usePaymentInvoicesTableFiltersStore('setBody');
    const setParams = usePaymentInvoicesTableFiltersStore('setParams');
    const getStatusesOptions =
        usePaymentInvoicesTableFiltersStore('getStatusesOptions');
    const paymentMethodNamesOptions = usePaymentInvoicesTableFiltersStore(
        'paymentMethodNamesFilter',
    );
    const siteIdentitiesOptions =
        usePaymentInvoicesTableFiltersStore('siteIdentities');
    const getMany = usePaymentInvoicesTableStore('getTable');
    const currenciesOptions = useMerchantsStore('currencyFilter');
    const merchantsOptions = useMerchantsStore('merchantNameFilter');

    const maxDate = dateHelpers.getCurrentDate();

    useEffect(() => {
        return () => {
            form.resetFields();

            setFormData(INITIAL_FILTERS);

            clearBody();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initStartDate = formData.createdAtFrom
        ? dateHelpers.formatDays(formData.createdAtFrom)
        : null;
    const initEndDate = formData.createdAtTo
        ? dateHelpers.formatDays(formData.createdAtTo)
        : null;

    const statusesOptions = getStatusesOptions(t);

    const initFinishedAtFromDate = formData.finishedAtFrom
        ? dateHelpers.formatDays(formData.finishedAtFrom)
        : null;
    const initFinishedAtToDate = formData.finishedAtTo
        ? dateHelpers.formatDays(formData.finishedAtTo)
        : null;

    const handleClearFilters = () => {
        form.resetFields();

        setFormData(INITIAL_FILTERS);

        clearBody();

        getMany({}, params);
    };

    const onSubmit = () => {
        if (!isValidDateRange(formData.createdAtFrom, formData.createdAtTo)) {
            return;
        }

        if (!isValidDateRange(formData.finishedAtFrom, formData.finishedAtTo)) {
            return;
        }

        const enhanceData = omitBy(
            filtersHelpers.getFiltersData(formData),
            (field) =>
                predicates.isUndefined(field) ||
                predicates.isEmptyString(field) ||
                predicates.isEmptyArray(field),
        );

        const newParams = { ...params, page: 0 };

        setBody(enhanceData);
        setParams(newParams);

        getMany(enhanceData, newParams);
    };

    const handleChangeField =
        <K extends keyof IFilterForm>(field: K) =>
        (value: IFilterForm[K]) => {
            setFormData((data) => ({
                ...data,
                [field]: value,
            }));
        };

    const handleChangeStartDate: DatePickerProps['onChange'] = async (
        _,
        startDate,
    ) => {
        if (Array.isArray(startDate)) {
            return;
        }

        handleChangeField('createdAtFrom')(startDate);
    };

    const handleChangeEndDate: DatePickerProps['onChange'] = async (
        _,
        endDate,
    ) => {
        if (Array.isArray(endDate)) {
            return;
        }

        handleChangeField('createdAtTo')(endDate);
    };

    const handleChangeSearchStr = handleChangeField('searchStr');
    const handleChangeExternalUserId = handleChangeField('externalUserId');
    const handleChangeExternalIdentity = handleChangeField('externalIdentity');
    const handleChangeAmountFrom = handleChangeField('amountFrom');
    const handleChangeAmountTo = handleChangeField('amountTo');
    const handleChangeCurrency = handleChangeField('currencyCode');
    const handleChangeStatuses = handleChangeField('statuses');
    const handleChangeMethodCodes = handleChangeField('methodCodes');
    const handleChangeSiteIdentities = handleChangeField('siteIdentities');
    const handleChangeSurchargeAmountFrom = handleChangeField(
        'surchargeAmountFrom',
    );
    const handleChangeSurchargeAmountTo =
        handleChangeField('surchargeAmountTo');

    const handleChangeFinishedAtFromDate: DatePickerProps['onChange'] = async (
        _,
        startDate,
    ) => {
        if (Array.isArray(startDate)) {
            return;
        }

        handleChangeField('finishedAtFrom')(startDate);
    };

    const handleChangeFinishedAtToDate: DatePickerProps['onChange'] = async (
        _,
        endDate,
    ) => {
        if (Array.isArray(endDate)) {
            return;
        }

        handleChangeField('finishedAtTo')(endDate);
    };

    const handleChangeMerchants = (
        merchantIdentities: IFilterForm['merchantIdentities'],
    ) => {
        setFormData((data) => ({
            ...data,
            merchantIdentities,
        }));
    };

    return (
        <Form form={form} onFinish={onSubmit} className={styles.leftControls}>
            <div className={styles.filtersContainer}>
                <Search
                    value={formData.searchStr}
                    onChange={handleChangeSearchStr}
                    className={styles.search}
                    placeholder={t('common.form.search')}
                />
                <Input
                    value={formData.externalUserId}
                    onChange={(e) => handleChangeExternalUserId(e.target.value)}
                    className={styles.search}
                    placeholder={t('common.form.userId')}
                />
                <Input
                    value={formData.externalIdentity}
                    onChange={(e) =>
                        handleChangeExternalIdentity(e.target.value)
                    }
                    className={styles.search}
                    placeholder={t('common.form.identity')}
                />
                <Input
                    value={formData.amountFrom}
                    onChange={(e) => handleChangeAmountFrom(e.target.value)}
                    className={styles.search}
                    placeholder={t('common.form.amountFrom')}
                />
                <Input
                    value={formData.amountTo}
                    onChange={(e) => handleChangeAmountTo(e.target.value)}
                    className={styles.search}
                    placeholder={t('common.form.amountTo')}
                />
                <SingleSelect
                    value={formData.currencyCode}
                    onChange={handleChangeCurrency}
                    placeholder={t('common.form.currency')}
                    options={currenciesOptions}
                    className={styles.select}
                />
                <MultiSelect
                    value={formData.statuses}
                    onChange={handleChangeStatuses}
                    placeholder={t('common.form.status')}
                    options={statusesOptions}
                    maxTagCount='responsive'
                    className={styles.select}
                />
                <MultiSelect
                    value={formData.methodCodes}
                    onChange={handleChangeMethodCodes}
                    placeholder={t('common.form.method')}
                    options={paymentMethodNamesOptions}
                    maxTagCount='responsive'
                    className={styles.select}
                />
                <MultiSelect
                    value={formData.siteIdentities}
                    onChange={handleChangeSiteIdentities}
                    placeholder={t('common.form.site')}
                    options={siteIdentitiesOptions}
                    maxTagCount='responsive'
                    className={styles.select}
                />
                <MultiSelect
                    value={formData.merchantIdentities}
                    onChange={handleChangeMerchants}
                    placeholder={t('common.form.merchant')}
                    options={merchantsOptions}
                    maxTagCount='responsive'
                    className={styles.select}
                />
                <Input
                    value={formData.surchargeAmountFrom}
                    onChange={(e) =>
                        handleChangeSurchargeAmountFrom(e.target.value)
                    }
                    className={styles.search}
                    placeholder={t('common.form.surchargeAmountFrom')}
                />
                <Input
                    value={formData.surchargeAmountTo}
                    onChange={(e) =>
                        handleChangeSurchargeAmountTo(e.target.value)
                    }
                    className={styles.search}
                    placeholder={t('common.form.surchargeAmountTo')}
                />
            </div>
            <div className={styles.filtersContainer}>
                <DatePicker
                    value={initStartDate}
                    onChange={handleChangeStartDate}
                    showTime
                    placeholder={t('common.form.dateFrom')}
                    className={styles.datePicker}
                    maxDate={maxDate}
                />
                <DatePicker
                    value={initEndDate}
                    onChange={handleChangeEndDate}
                    showTime
                    placeholder={t('common.form.dateTo')}
                    className={styles.datePicker}
                    maxDate={maxDate}
                />
                <DatePicker
                    value={initFinishedAtFromDate}
                    onChange={handleChangeFinishedAtFromDate}
                    showTime
                    placeholder={t('common.form.finishedAtFromDate')}
                    className={styles.finalDatePicker}
                    maxDate={maxDate}
                />
                <DatePicker
                    value={initFinishedAtToDate}
                    onChange={handleChangeFinishedAtToDate}
                    showTime
                    placeholder={t('common.form.finishedAtToDate')}
                    className={styles.finalDatePicker}
                    maxDate={maxDate}
                />
            </div>
            <div className={styles.controlsContainer}>
                <div className={styles.buttonsContainer}>
                    <Button type='default' onClick={handleClearFilters}>
                        {t('common.buttons.clear')}
                    </Button>
                    <Button type='primary' htmlType='submit'>
                        {t('common.buttons.apply')}
                    </Button>
                </div>

                <div className={styles.rightControls}>
                    <InvoicesExportSettingsModalButton />
                    <InvoicesExportModalButton sendReport={onExport} />
                </div>
            </div>
        </Form>
    );
};
